import "pepjs";
import React, {
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { APIContextProvider } from "./APIContext";
import { APIDataContextProvider } from "./APIDataContext";
import BaseLayout from "./BaseLayout";
import {
	DtoLoginRequest,
	DtoLoginResponse,
	DtoLogoutResponse,
	DtoUser, InternalCatalogueAPI
} from "./catadmin/InternalCatalogueAPI";
import { ErrorBoundary } from "./components/ErrorBondary";
import { GlobalErrorContext } from "./GlobalErrorContext";
import { OverlayLoading } from "./icons/Loading";
import layoutStyles from "./Layout.module.css";
import { cacheLoginResponse, getFeaturesFromLogin, loginOffline } from "./lib/AdminAPI";
import * as ls from "./lib/localStorage";
import { LOGIN_URL_KEY } from "./lib/useLoginUrlFormInput";
import { NotFound404 } from "./NotFound404";

const Chair = React.lazy(() => import("./myconfigura/Chair"));
const AdminRoutes = React.lazy(() => import("./admin/AdminRoutes"));
const CataloguesRoutes = React.lazy(() => import("./CataloguesRoutes"));
const CmsymViewer = React.lazy(() => import("./CmsymViewer"));
const Home = React.lazy(() => import("./Home"));
const Login = React.lazy(() => import("./Login"));
const Settings = React.lazy(() => import("./Settings"));

export const InternalCatalogueAPIContext = React.createContext<InternalCatalogueAPI>(
	new InternalCatalogueAPI()
);
export const FeatureContext = React.createContext<string[]>([]);
export const UserContext = React.createContext<Option<DtoUser>>(undefined);
export const AppContext = React.createContext<
	Option<{
		login: (url: string, req: DtoLoginRequest) => Promise<DtoLoginResponse>;
		logout: () => Promise<DtoLogoutResponse>;
	}>
>(undefined);

interface Props {}

function App(props: Props) {
	const { setError } = useContext(GlobalErrorContext);

	const internalCatalogueAPI = useRef<InternalCatalogueAPI>(new InternalCatalogueAPI());
	const [loginResponse, setLoginResponse] = useState<DtoLoginResponse | undefined>(loginOffline);
	const [refreshUser, setRefreshUser] = useState(true);

	if (loginResponse !== undefined) {
		internalCatalogueAPI.current.auth = {
			endpoint: loginResponse.endpoint,
			features: loginResponse.session.features,
			secretToken: loginResponse.secretToken,
		};

		// you can uncomment this and replace the IP to test against a canary catproxy
		// loginResponse.auth.endpoint = "http://172.31.9.85";
	}

	const login = useCallback((url: string, req: DtoLoginRequest) => {
		return internalCatalogueAPI.current.postLogin(url, req).then((res) => {
			setRefreshUser(false);
			cacheLoginResponse(res);
			setLoginResponse(res);
			return res;
		});
	}, []);

	const logout = useCallback(() => {
		const loginUrl = ls.get(LOGIN_URL_KEY);
		ls.clear();
		if (loginUrl) {
			ls.set(LOGIN_URL_KEY, loginUrl);
		}

		if (loginResponse !== undefined && loginResponse.secretToken === "") {
			setLoginResponse(undefined);
			return Promise.reject();
		}

		return internalCatalogueAPI.current.getLogout().then((res) => {
			setLoginResponse(undefined);
			return res;
		});
	}, [loginResponse]);

	const updateUser = useCallback(() => {
		if (internalCatalogueAPI.current.auth === undefined) {
			return Promise.reject();
		}
		return internalCatalogueAPI.current.getUser().then((res) => {
			setLoginResponse((prev) => {
				if (prev === undefined) {
					return prev;
				}
				return { ...prev, session: { ...prev.session, user: res.user } };
			});
			return res;
		});
	}, []);

	const appContextValue = useMemo(() => {
		return { login, logout };
	}, [login, logout]);

	useEffect(() => {
		if (!refreshUser || loginResponse === undefined) {
			return;
		}

		if (loginResponse.secretToken !== "") {
			setRefreshUser(false);
			updateUser().catch((err) => {
				setLoginResponse(undefined);
				setError("could not update user", err instanceof Error ? err : undefined);
			});
		}
	}, [loginResponse, setError, updateUser, refreshUser]);

	if (loginResponse === undefined) {
		return (
			<React.StrictMode>
				<Router>
					<Routes>
						<Route
							path="chair"
							element={
								<Suspense fallback={<></>}>
									<Chair />
								</Suspense>
							}
						></Route>
						<Route
							path="*"
							element={
								<Suspense fallback={<OverlayLoading />}>
									<div className={layoutStyles.homeContainer}>
										<div className={layoutStyles.homeContent}>
											<Login login={login} />
										</div>
									</div>
								</Suspense>
							}
						></Route>
					</Routes>
				</Router>
			</React.StrictMode>
		);
	}

	const features = getFeaturesFromLogin();
	const permissions = loginResponse.auth.apiSession.permissions || [];

	return (
		<React.StrictMode>
			<AppContext.Provider value={appContextValue}>
				<APIContextProvider auth={loginResponse.auth}>
					<InternalCatalogueAPIContext.Provider value={internalCatalogueAPI.current}>
						<APIDataContextProvider>
							<UserContext.Provider value={loginResponse.session.user}>
								<FeatureContext.Provider value={features}>
									<ErrorBoundary>
										<Suspense fallback={<OverlayLoading />}>
											<Router>
												<Routes>
													<Route
														path="chair"
														element={
															<Suspense fallback={<></>}>
																<Chair />
															</Suspense>
														}
													></Route>
													<Route path="" element={<Home />} />
													<Route
														path="settings"
														element={
															<Settings setAuth={setLoginResponse} />
														}
													/>
													{features.includes("superuser") && (
														<Route
															path="admin/*"
															element={<AdminRoutes />}
														/>
													)}
													<Route
														path="catalogue/*"
														element={
															<CataloguesRoutes
																permissions={permissions}
															/>
														}
													/>
													<Route
														path="cmsym-viewer"
														element={<CmsymViewer />}
													/>

													<Route
														path="*"
														element={
															<BaseLayout>
																<NotFound404></NotFound404>
															</BaseLayout>
														}
													/>
												</Routes>
											</Router>
										</Suspense>
									</ErrorBoundary>
								</FeatureContext.Provider>
							</UserContext.Provider>
						</APIDataContextProvider>
					</InternalCatalogueAPIContext.Provider>
				</APIContextProvider>
			</AppContext.Provider>
		</React.StrictMode>
	);
}

export default App;
